import {Injectable} from '@angular/core';
import {TextDetection} from './ocr.service';

export interface IResizeImageOptions {
  maxSize: number;
  b64: string;
}

@Injectable()
export class ImageManipulationService {

  resizeImage(settings: IResizeImageOptions) {
    const b64 = settings.b64;
    const maxSize = settings.maxSize;
    const image = new Image();
    const canvas = document.createElement('canvas');

    const resize = () => {
      let width = image.width;
      let height = image.height;

      if (width > height) {
        if (width > maxSize) {
          height *= maxSize / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width *= maxSize / height;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(image, 0, 0, width, height);
      return canvas.toDataURL('image/jpeg');
    };

    return new Promise((resolve, reject) => {
      image.onload = () => resolve(resize());
      image.src = b64;
    });
  }

  resetOrientation(file): Promise<{result: string, orientation: number}> {

    const reader = new FileReader();

    if (typeof file === 'undefined') {
      return Promise.reject('No file');
    }

    if (file instanceof ArrayBuffer) {
      return Promise.reject('Cannot handle ArrayBuffer, String required');
    }

    const image = new Image();

    return new Promise((resolve, reject) => {
      reader.onload = (readerEvent: any) => {
        image.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = image.width;
          canvas.height = image.height;
          canvas.getContext('2d').drawImage(image, 0, 0);
          resolve({result: canvas.toDataURL(), orientation: null});
        };
        image.src = readerEvent.target.result;
      };
      reader.readAsDataURL(file);
    });
  }


  getOrientation(file): Promise<number> {
    const reader = new FileReader();

    return new Promise(((resolve, reject) => {
      reader.onload = function (event) {
        const view = new DataView(reader.result as ArrayBuffer);

        if (view.getUint16(0, false) != 0xFFD8) {
          resolve(-2);
          return;
        }

        const length = view.byteLength;
        let offset = 2;

        while (offset < length) {
          const marker = view.getUint16(offset, false);
          offset += 2;

          if (marker == 0xFFE1) {
            if (view.getUint32(offset += 2, false) != 0x45786966) {
              resolve(-1);
              return;
            }
            const little = view.getUint16(offset += 6, false) == 0x4949;
            offset += view.getUint32(offset + 4, little);
            const tags = view.getUint16(offset, little);
            offset += 2;

            for (let i = 0; i < tags; i++) {
              if (view.getUint16(offset + (i * 12), little) == 0x0112) {
                resolve(view.getUint16(offset + (i * 12) + 8, little));
                return;
              }
            }
          } else if ((marker & 0xFF00) != 0xFF00) {
            break;
          } else {
            offset += view.getUint16(offset, false);
          }
        }
        resolve(-1);
      };

      reader.readAsArrayBuffer(file.slice(0, 64 * 1024));
    }));
  }

  cropImage(base64Image: string, filteredResult: TextDetection) {
    const canvasOriginal = document.createElement('canvas');
    const ctx = canvasOriginal.getContext('2d');
    const imageObj = new Image();

    return new Promise(((resolve, reject) => {

      const self = this;

      imageObj.onload = function () {
        const polygon = filteredResult.Geometry.Polygon;
        resolve(self.clipIt(canvasOriginal, ctx, polygon, imageObj));
      };
      imageObj.src = base64Image;
    }));
  }

  clipIt(canvas, ctx, points, img) {
    // calculate the size of the user's clipping area
    let minX = 10000;
    let minY = 10000;
    let maxX = -10000;
    let maxY = -10000;
    const cw = canvas.width = img.width;
    const ch = canvas.height = img.height;

    for (let i = 1; i < points.length; i++) {
      const p = points[i];
      if (p.X * cw < minX) {
        minX = p.X * cw;
      }
      if (p.Y * ch < minY) {
        minY = p.Y * ch;
      }
      if (p.X * cw > maxX) {
        maxX = p.X * cw;
      }
      if (p.Y * ch > maxY) {
        maxY = p.Y * ch;
      }
    }

    console.log(JSON.stringify(points));
    const width = maxX - minX;
    const height = maxY - minY;

    // clip the image into the user's clipping area
    ctx.save();
    ctx.clearRect(0, 0, cw, ch);
    ctx.beginPath();
    ctx.moveTo(points[0].X * cw, points[0].Y * ch);
    for (let i = 1; i < points.length; i++) {
      const p = points[i];
      ctx.lineTo(points[i].X * cw, points[i].Y * ch);
    }
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(img, 0, 0);
    ctx.restore();

    // create a new canvas
    const c = document.createElement('canvas');
    const cx = c.getContext('2d');

    // resize the new canvas to the size of the clipping area
    c.width = width;
    c.height = height;

    // draw the clipped image from the main canvas to the new canvas
    cx.drawImage(canvas, minX, minY, width, height, 0, 0, width, height);

    return c.toDataURL();
  }
}
